package it.unibo.tuprolog.solve.stdlib.rule

import it.unibo.tuprolog.core.Scope
import it.unibo.tuprolog.core.Term
import it.unibo.tuprolog.solve.ExecutionContext
import it.unibo.tuprolog.solve.Signature
import it.unibo.tuprolog.solve.rule.RuleWrapper
import kotlin.collections.List as KtList
import kotlin.collections.listOf as ktListOf

sealed class Append : RuleWrapper<ExecutionContext>(FUNCTOR, ARITY) {
    override val help: String =
        """
        `append(?Prefix, ?Suffix, ?Whole)`
        
        Relates two lists with their concatenation: `Whole` is obtained by appending `Suffix` to `Prefix`. The relation is reversible and can be used to concatenate lists or enumerate splits of a list.

        **Examples**

        ```prolog
        ?- append([1, 2], [3], L).
        L = [1, 2, 3].

        ?- append(X, [3], [1, 2, 3]).
        X = [1, 2].

        ?- append(X, Y, [1, 2]).
        X = [], Y = [1, 2] ; X = [1], Y = [2] ; X = [1, 2], Y = [].
        ```
        """.trimIndent()

    abstract override val Scope.head: KtList<Term>

    object Base : Append() {
        override val Scope.head: KtList<Term>
            get() =
                ktListOf(
                    emptyLogicList,
                    varOf("X"),
                    varOf("X"),
                )
    }

    object Recursive : Append() {
        override val Scope.head: KtList<Term>
            get() =
                ktListOf(
                    consOf(varOf("X"), varOf("Y")),
                    varOf("Z"),
                    consOf(varOf("X"), varOf("W")),
                )

        override val Scope.body: Term
            get() = structOf("append", varOf("Y"), varOf("Z"), varOf("W"))
    }

    companion object {
        const val FUNCTOR: String = "append"
        const val ARITY: Int = 3
        val SIGNATURE: Signature
            get() = Signature(FUNCTOR, ARITY)
    }
}
